import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { useMutation } from "react-query";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import PageTitle from "../../Components/PageTitle";
import PaySection from "./sections/PaySection";
import CreateSection from "./sections/CreateSection";
import CollaborateSection from "./sections/CollaborateSection";
import BentoSection from "./sections/BentoSection";
import {FastAPI, Dribbble, Mintlify, Facebook, JetbrainsSpace, HuggingFace} from "../../Utils/icons";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import client from "../../API";
import routes from "../../Config/routes";
import { replaceConsoleWarn, replaceConsoleError } from '../../Utils/toastNotifications';
import {
  initAuthStorage,
  getAuthToken as getUserToken,
} from "../../API/token-util";
import {
  initCreatorAuthStorage,
  getAuthToken as getCreatorToken,
} from "../../API/creatorTokenUtil";
import lightninggif from "../../Components/globalAssets/lightning3.gif";
import FeaturedArticles from "./sections/articles";
import Globe from "./../../Pages/Dashboard/dashboard/globe";
// ─────────────────────────────────────────────────────────────────────────────
// Optimized constants and helpers
// ─────────────────────────────────────────────────────────────────────────────
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const titleWords = ["PAY", "CREATE", "COLLABORATE"];
const brandNames = [
  { name: "FastAPI", icon: <FastAPI /> },
  { name: "Dribbble", icon: <Dribbble /> },
  { name: "Mintlify", icon: <Mintlify /> },
  { name: "Facebook", icon: <Facebook /> },
  { name: "Jetbrains Space", icon: <JetbrainsSpace /> },
  { name: "Hugging Face", icon: <HuggingFace /> },
];
const getFontSize = (word) => {
  const len = word.length;
  if (len <= 4) return { xs: "22vw", sm: "18vw", md: "8vw", lg: "6vw" }; // PAY
  if (len <= 7) return { xs: "18vw", sm: "14vw", md: "6vw", lg: "4.5vw" }; // CREATE
  return { xs: "10vw", sm: "6vw", md: "2vw", lg: "1.5vw" }; // COLLABORATE
};

// Enhanced Lightning component with guaranteed infinite looping
const LightningAnimation = ({ size = { xs: 200, sm: 250, md: 300, lg: 400 } }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const imgRef = useRef(null);

  // Force refresh the GIF every 3 seconds to ensure looping
  useEffect(() => {
    if (!isLoaded || hasError) return;

    const interval = setInterval(() => {
      if (imgRef.current) {
        // Force GIF restart by changing the src slightly
        const timestamp = Date.now();
        const originalSrc = lightninggif;
        imgRef.current.src = `${originalSrc}?t=${timestamp}`;
      }
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [isLoaded, hasError]);

  // Fallback to CSS animation if GIF fails
  const handleError = useCallback(() => {
    replaceConsoleWarn("Lightning GIF failed to load, using CSS fallback");
    setHasError(true);
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
  }, []);

  // CSS-based lightning animation fallback
  const CSSLightningAnimation = () => (
    <Box
      sx={{
        width: size,
        height: size,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
            color: "#ffffff",
            textShadow: "0 0 10px #fff, 0 0 20px #fff, 0 0 30px #00f3ff",
            animation: `lightning${index} ${2 + index * 0.5}s infinite ease-in-out`,
            animationDelay: `${index * 0.3}s`,
            "@keyframes lightning0": {
              "0%, 100%": {
                opacity: 0.3,
                transform: "scale(0.8) rotate(-5deg)",
                filter: "brightness(0.7)"
              },
              "50%": {
                opacity: 1,
                transform: "scale(1.2) rotate(5deg)",
                filter: "brightness(1.5)"
              },
            },
            "@keyframes lightning1": {
              "0%, 100%": {
                opacity: 0.2,
                transform: "scale(0.9) rotate(3deg)",
                filter: "brightness(0.8)"
              },
              "30%": {
                opacity: 0.8,
                transform: "scale(1.1) rotate(-3deg)",
                filter: "brightness(1.3)"
              },
              "70%": {
                opacity: 0.6,
                transform: "scale(1.05) rotate(2deg)",
                filter: "brightness(1.1)"
              },
            },
            "@keyframes lightning2": {
              "0%, 100%": {
                opacity: 0.1,
                transform: "scale(1) rotate(0deg)",
                filter: "brightness(0.9)"
              },
              "25%": {
                opacity: 0.7,
                transform: "scale(0.95) rotate(-2deg)",
                filter: "brightness(1.2)"
              },
              "75%": {
                opacity: 0.9,
                transform: "scale(1.15) rotate(4deg)",
                filter: "brightness(1.4)"
              },
            },
          }}
        >
          ⚡
        </Box>
      ))}

      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle, transparent 30%, rgba(0,243,255,0.1) 70%)",
          animation: "electricField 4s infinite ease-in-out",
          "@keyframes electricField": {
            "0%, 100%": {
              opacity: 0.2,
              transform: "scale(1)",
            },
            "50%": {
              opacity: 0.5,
              transform: "scale(1.1)",
            },
          },
        }}
      />
    </Box>
  );

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      whileHover={{ scale: 1.05 }}
      style={{ position: "relative" }}
    >
      {hasError ? (
        <CSSLightningAnimation />
      ) : (
        <>
          <Box
            ref={imgRef}
            component="img"
            src={`${lightninggif}?t=${refreshKey}`}
            alt="Lightning animation"
            onLoad={handleLoad}
            onError={handleError}
            sx={{
              width: size,
              height: size,
              objectFit: "contain",
              filter: "drop-shadow(0 0 20px rgba(255,255,255,0.3))",
              borderRadius: 2,
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.3s ease",
              imageRendering: "auto",
              userSelect: "none",
              pointerEvents: "none",
            }}
            style={{
              imageRendering: "-webkit-optimize-contrast",
              backfaceVisibility: "hidden",
              transform: "translateZ(0)",
              maxWidth: "none",
            }}
          />

          {!isLoaded && !hasError && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  fontSize: "4rem",
                  color: "#ffffff",
                  textShadow: "0 0 10px #fff, 0 0 20px #00f3ff",
                  animation: "loadingPulse 1.5s infinite ease-in-out",
                  "@keyframes loadingPulse": {
                    "0%, 100%": {
                      opacity: 0.3,
                      transform: "scale(0.9)",
                      filter: "brightness(0.7)"
                    },
                    "50%": {
                      opacity: 1,
                      transform: "scale(1.1)",
                      filter: "brightness(1.5)"
                    },
                  },
                }}
              >
                ⚡
              </Box>
            </Box>
          )}
        </>
      )}
    </motion.div>
  );
};

// Reduced floating elements with CSS animations for better performance
const FloatingElements = React.memo(() => {
  const elements = useMemo(() =>
    [...Array(4)].map((_, i) => ({
      id: i,
      size: 60 + Math.random() * 40,
      opacity: Math.floor(Math.random() * 3) + 1,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: i * 1.5,
    })), []
  );

  return (
    <>
      {elements.map((el) => (
        <Box
          key={el.id}
          sx={{
            position: "absolute",
            width: `${el.size}px`,
            height: `${el.size}px`,
            borderRadius: "50%",
            background: `rgba(255,255,255,0.${el.opacity})`,
            top: `${el.top}%`,
            left: `${el.left}%`,
            filter: "blur(30px)",
            zIndex: 0,
            animation: `float${el.id} ${6 + el.id}s infinite ease-in-out`,
            animationDelay: `${el.delay}s`,
            "@keyframes float0": {
              "0%, 100%": { transform: "translate(0, 0)" },
              "33%": { transform: "translate(-10px, -20px)" },
              "66%": { transform: "translate(10px, 20px)" },
            },
            "@keyframes float1": {
              "0%, 100%": { transform: "translate(0, 0)" },
              "50%": { transform: "translate(15px, -15px)" },
            },
            "@keyframes float2": {
              "0%, 100%": { transform: "translate(0, 0)" },
              "25%": { transform: "translate(-15px, 10px)" },
              "75%": { transform: "translate(10px, -10px)" },
            },
            "@keyframes float3": {
              "0%, 100%": { transform: "translate(0, 0)" },
              "40%": { transform: "translate(8px, 15px)" },
              "80%": { transform: "translate(-8px, -15px)" },
            },
          }}
        />
      ))}
    </>
  );
});

// Simplified transition component
const SimpleTransition = React.memo(({ gradient, height = "15vh" }) => (
  <Box
    sx={{
      height,
      background: gradient,
      position: "relative",
      overflow: "hidden"
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        width: "100px",
        height: "2px",
        background: "rgba(255,255,255,0.4)",
        animation: "slide 4s infinite linear",
        "@keyframes slide": {
          "0%": { left: "-100px" },
          "100%": { left: "calc(100% + 100px)" },
        },
      }}
    />
  </Box>
));

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const platform = Capacitor.getPlatform();
  const isIOS = platform === "ios";

  // Refs for section scrolling
  const heroRef = useRef(null);
  const payRef = useRef(null);
  const createRef = useRef(null);
  const collaborateRef = useRef(null);

  // ── Optimized state management ─────────────
  const [wordIndex, setWordIndex] = useState(0);
  const [brandIndex, setBrandIndex] = useState(0);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);

  // ── Auth hooks ─────────────────────────────────────────────────────────────
  const { authorize, isAuthorized, setCurrentUser } = useAuth();
  const { login: creatorLogin, isCreatorAuthorized } = useCreatorAuth();

  const { mutate: unifiedLogin, isLoading: isLoggingIn } = useMutation(
    client.users.uniloginV2,
    {
      onSuccess: (data) => {
        // Check if 2FA is required
        if (data.status === '2fa_required') {
          replaceConsoleError("[UnifiedLogin] 2FA required but not supported in Home.js");
          navigate(routes.login, {
            replace: true,
            state: { message: 'Two-factor authentication required. Please log in.' }
          });
          return;
        }

        // Check if account linking is required
        if (data.status === 'link_required') {
          replaceConsoleError("[UnifiedLogin] Account linking required");
          navigate(routes.login, {
            replace: true,
            state: { message: 'Please complete account linking from the login page.' }
          });
          return;
        }

        // Defensive check: ensure data.user exists
        if (!data.user || !data.user.global_user_type) {
          replaceConsoleError("[UnifiedLogin] Invalid login response:", data);
          navigate(routes.login, {
            replace: true,
            state: { message: 'Login failed. Please try again.' }
          });
          return;
        }

        // Original logic
        if (data.user.global_user_type === "creator") {
          creatorLogin({ token: data.token, creator_user: data.user });
          navigate(routes.creatorConnectStart, { replace: true });
        } else {
          authorize(data.token);
          setCurrentUser(data.user);
          navigate(routes.dashboard, { replace: true });
        }
      },
      onError: (error) => {
        replaceConsoleError("[UnifiedLogin] Login error:", error);
      },
    }
  );

  // ── Token-based redirection ────────────────────────────────────────────────
  useEffect(() => {
    const checkTokensAndRedirect = async () => {
      try {
        await Promise.all([initAuthStorage(), initCreatorAuthStorage()]);
        const creatorJWT = getCreatorToken();
        const userJWT = getUserToken();
        const isRoot =
          window.location.pathname === "/" ||
          window.location.pathname === routes.base;
        if (isRoot && (creatorJWT || userJWT)) {
          const startPath = creatorJWT
            ? routes.creatorConnectStart
            : routes.dashboard;
          navigate(startPath, { replace: true });
        }
      } catch (error) {
        replaceConsoleError("[Home] Token check error:", error);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    if (!isAuthorized && !isCreatorAuthorized) {
      checkTokensAndRedirect();
    } else {
      setIsLoadingTokens(false);
    }
  }, [navigate, isAuthorized, isCreatorAuthorized]);

  // ── Callbacks ──────────────────────────────────────────────────────────────
  const handleLogin = useCallback(() => navigate("/login"), [navigate]);
  const handleSignup = useCallback(() => navigate("/onboard"), [navigate]);

  // ── Google One-Tap Login ───────────────────────────────────────────────────
  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      const token = credentialResponse.credential;
      if (!isAuthorized && !isCreatorAuthorized && !isLoggingIn) {
        unifiedLogin({ token });
      }
    },
    disabled: isAuthorized || isCreatorAuthorized || isLoggingIn,
    onError: () => {
      console.debug("[Google One-Tap] Login failed");
    },
  });

  // ── Native silent refresh ──────────────────────────────────────────────────
  useEffect(() => {
    const tryNativeRefresh = async () => {
      if (
        Capacitor.getPlatform() !== "web" &&
        !isAuthorized &&
        !isCreatorAuthorized &&
        !isLoggingIn
      ) {
        try {
          // Placeholder for GoogleAuth.refresh() when implemented
        } catch (e) {
          console.debug("No existing native Google session:", e);
        }
      }
    };
    tryNativeRefresh();
  }, [isAuthorized, isCreatorAuthorized, isLoggingIn]);

  // ── Optimized word rotation effect ────────────────────────────────────
  useEffect(() => {
    const id = setInterval(
      () => setWordIndex((i) => (i + 1) % titleWords.length),
      2500
    );
    return () => clearInterval(id);
  }, []);

  // Brand animation cycling
  useEffect(() => {
    const id = setInterval(
      () => setBrandIndex((i) => (i + 1) % brandNames.length),
      2000
    );
    return () => clearInterval(id);
  }, []);

  // ── Optimized scroll tracking with throttling ──────────────────────────────────────────────────
  useEffect(() => {
    if (isIOS) return;

    const sections = [heroRef, payRef, createRef, collaborateRef];
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY + window.innerHeight / 2;

          sections.forEach((ref, index) => {
            if (ref.current) {
              const sectionTop = ref.current.offsetTop;
              const sectionBottom = ref.current.offsetHeight;

              if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
                if (currentSection !== index) {
                  setCurrentSection(index);
                }
              }
            }
          });

          ticking = false;
        });
        ticking = true;
      }
    };

    // Throttled scroll listener
    let timeoutId;
    const throttledHandleScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 16); // ~60fps
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isIOS, currentSection]);

  // ── Optimized scroll to section function ─────────────────────────────────────────────
  const scrollToSection = useCallback((ref) => {
    if (!ref.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  // ── Memoized button handlers ───────────────────────────────────────────────
  const memoizedButtons = useMemo(() => ({
    startButton: (
      <Button
        variant="contained"
        size="large"
        onClick={handleSignup}
        sx={{
          fontWeight: 700,
          px: 4,
          py: 1.5,
          background: "linear-gradient(45deg, #ff4d4d, #ff1a75)",
          boxShadow: "0 8px 24px rgba(255, 77, 77, 0.3)",
          width: { xs: "100%", sm: "auto" },
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 32px rgba(255, 77, 77, 0.4)",
          }
        }}
      >
        Start Now
      </Button>
    ),
    learnButton: (
      <Button
        variant="outlined"
        size="large"
        onClick={() => scrollToSection(payRef)}
        sx={{
          fontWeight: 600,
          px: 4,
          py: 1.5,
          borderColor: "rgba(255,255,255,0.3)",
          color: "rgba(255,255,255,0.9)",
          borderWidth: 2,
          width: { xs: "100%", sm: "auto" },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.1)",
            borderColor: "rgba(255,255,255,0.5)",
            borderWidth: 2,
          },
        }}
      >
        Learn More
      </Button>
    )
  }), [handleSignup, scrollToSection, payRef]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoadingTokens) {
    return null;
  }

  // iOS: simplified hero layout; avoids heavy effects for better perf
  if (isIOS) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(to bottom, #ff4d4d 0%, #ffffff 100%)",
        }}
      >
        <HomeHeader />
        <PageTitle
          title="Blitz – The Future of Work is Here"
          description="Instant invoice advances and smooth settlement for creators and brands. Proposals, contracts, deliverables, and link‑in‑bio flow into BlitzPay."
          canonical="https://www.useblitz.co/home"
          openGraph={{ image: "/thumbnail.png", url: "https://www.useblitz.co/home" }}
          twitter={{ card: "summary_large_image" }}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 6,
            pb: 16,
            px: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              width: "100%",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={wordIndex}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.7, ease: "easeOut" },
                }}
                exit={{
                  opacity: 0,
                  y: -40,
                  scale: 0.9,
                  transition: { duration: 0.6, ease: "easeIn" },
                }}
                style={{ display: "inline-block", width: "100%" }}
              >
                <Typography
                  id="home-hero-heading-ios"
                  component="h1"
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    fontSize: getFontSize(titleWords[wordIndex]),
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    background: "linear-gradient(45deg, #ff4d4d, #3a1c71)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    textTransform: "uppercase",
                  }}
                >
                  {titleWords[wordIndex]}
                </Typography>
              </motion.span>
            </AnimatePresence>
          </Box>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              maxWidth: "100%",
              fontSize: "1.1rem",
              lineHeight: "1.6rem",
              color: "#333",
            }}
          >
            Blitz is your all-in-one platform for instant payouts, data-driven
            campaigns, and seamless global teamwork.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              maxWidth: 320,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{
                fontSize: "1.1rem",
                fontWeight: 700,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 10px rgba(255, 77, 77, 0.3)",
                background: "linear-gradient(45deg, #ff4d4d, #ff1a75)",
              }}
            >
              Login
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleSignup}
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                borderWidth: 2,
                color: "#ff4d4d",
                borderColor: "#ff4d4d",
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "rgba(255, 77, 77, 0.05)",
                },
              }}
            >
              Create an Account
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop + non‑iOS mobile: full hero and sections layout
  return (
    <Box sx={{
      overflowX: "hidden",
      scrollBehavior: "smooth",
      background:theme.palette.background.default 
    }}>
      <HomeHeader />
      <PageTitle
        title="Blitz – The Future of Work is Here"
        description="Instant invoice advances and smooth settlement for creators and brands. Proposals, contracts, deliverables, and link‑in‑bio flow into BlitzPay."
        canonical="https://www.useblitz.co/home"
        openGraph={{ image: "/thumbnail.png", url: "https://www.useblitz.co/home" }}
        twitter={{ card: "summary_large_image" }}
      />

      {/* Hero Section - Completely Redesigned */}
      <Box
        ref={heroRef}
        component={motion.section}
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ duration: 0.8, ease: "linear" }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "auto", md: "100vh" },
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 8, sm: 10, md: 8 },
          background: theme.palette.background.default ,
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Animated grid background - subtle pattern for light theme */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: { xs: "30px 30px", md: "40px 40px" },
            animation: "gridMove 25s linear infinite",
            "@keyframes gridMove": {
              "0%": { transform: "translate(0, 0)" },
              "100%": { transform: "translate(40px, 40px)" }
            }
          }}
        />

        {/* Glowing orbs for depth - subtle accent for light theme */}
        <Box sx={{ 
          display: "flex", 
          width: "100%", 
          height: "100%", 
          justifyContent: "space-between", 
          alignItems: "center",
          flexDirection: { xs: "column", md: "row" }
        }}>
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              left: "5%",
              width: { xs: "200px", sm: "300px", md: "600px" },
              height: { xs: "200px", sm: "300px", md: "600px" },
              background: "radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 60%)",
              filter: "blur(80px)",
              animation: "float 12s ease-in-out infinite",
              "@keyframes float": {
                "0%, 100%": { transform: "translate(0, 0)" },
                "50%": { transform: "translate(20px, -20px)" }
              }
            }} 
          />
          <Box 
            sx={{
              position: "absolute",
              bottom: "5%",
              right: "5%",
              width: { xs: "250px", sm: "350px", md: "700px" },
              height: { xs: "250px", sm: "350px", md: "700px" },
              background: "radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 60%)",
              filter: "blur(100px)",
              animation: "float 15s ease-in-out infinite reverse"
            }} 
          />

          <Box
            role="region"
            aria-labelledby="home-hero-heading"
            sx={{
              maxWidth: 1400,
              width: "100%",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Eyebrow text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#64748b",
                  fontSize: { xs: "0.625rem", sm: "0.75rem", md: "0.875rem" },
                  fontWeight: 600,
                  letterSpacing: { xs: "0.1em", md: "0.15em" },
                  mt: { xs: 2, sm: 3, md: 4 },
                  display: "block",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 0 10px 0 rgba(0,0,0,0.05)",
                  borderRadius: "50px",
                  padding: { xs: "2px 8px", md: "2px 10px" },
                  width: "fit-content",
                  background: "white"
                }}
              >
                For Winners Worldwide
              </Typography>
            </motion.div>

            {/* Main animated heading */}
            <Box sx={{
              mb: { xs: 2, sm: 3, md: 4 },
              minHeight: { xs: "auto", md: "180px" },
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              flexDirection: "column"
            }}>
              <Typography
                sx={{
                  textAlign: "left",
                  fontWeight: 500,
                  fontFamily: "Moret Variable",
                  color: "#0f172a",
                  letterSpacing: { xs: "0.02em", md: "0.05em" },
                  fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem", lg: "4.5rem" },
                  lineHeight: { xs: 1.2, md: 1.1 },
                  py: { xs: 2, sm: 3, md: 3 },
                }}
                variant="h1" 
                id="home-hero-heading"
              >
                The payments
                ecosystem <br />
                For the <Box 
                  component="span" 
                  sx={{ color: "#7B2A2A" }}
                >
                  new economy
                </Box>
              </Typography>
            </Box>

            {/* Subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Typography
                component="p"
                role="doc-subtitle"
                variant="h5"
                sx={{
                  mb: { xs: 4, sm: 5, md: 6 },
                  maxWidth: { xs: "100%", md: 700 },
                  color: "#475569",
                  lineHeight: { xs: 1.6, md: 1.7 },
                  fontSize: { xs: "0.9375rem", sm: "1.125rem", md: "1.5rem" },
                  fontWeight: 400,
                  textAlign: "left"
                }}
              >
                The all-in-one platform for instant payouts, data-driven campaigns, and seamless collaboration
              </Typography>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Box sx={{
                display: "flex",
                gap: { xs: 2, sm: 3 },
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "flex-start" },
                justifyContent: "flex-start",
                mb: { xs: 4, sm: 6, md: 8 }
              }}>
                <Button
                  aria-label="Get started with Blitz"
                  autoFocus
                  variant="contained"
                  size="large"
                  onClick={handleSignup}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                    px: { xs: 3, sm: 4, md: 6 },
                    py: { xs: 1.5, sm: 1.75, md: 2 },
                    borderRadius: "50px",
                    minWidth: { xs: "auto", sm: "180px", md: "200px" },
                    width: { xs: "100%", sm: "auto" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.12)", md: "0 12px 40px rgba(0,0,0,0.15)" },
                    textTransform: "none",
                    '&::before': {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s",
                    },
                    '&:hover': {
                      bgcolor: theme.palette.primary.light,
                      transform: { xs: "none", sm: "translateY(-4px) scale(1.02)" },
                      boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.15)", md: "0 16px 56px rgba(0,0,0,0.2)" },
                      '&::before': {
                        left: "100%"
                      }
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>
                    Get Started Free
                  </span>
                </Button>
                <Button
                  aria-label="Learn more about Blitz"
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection(payRef)}
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                    px: { xs: 3, sm: 4, md: 6 },
                    py: { xs: 1.5, sm: 1.75, md: 2 },
                    border: "1px solid #cbd5e1",
                    color: "#334155",
                    borderRadius: "50px",
                    minWidth: { xs: "auto", sm: "180px", md: "200px" },
                    width: { xs: "100%", sm: "auto" },
                    background: "white",
                    textTransform: "none",
                    '&:hover': {
                      background: '#f1f5f9',
                      borderColor: '#94a3b8',
                      color: "#0f172a",
                      transform: { xs: "none", sm: "translateY(-4px)" }
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  View Features
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Globe - hidden on mobile */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Globe
              size={400}
              color="#0f172a"
              backgroundColor="#f0f4f8"
              borderColor="#f8fafc"
              borderWidth={1}
              borderRadius={10}
              borderStyle="solid"
            />
          </Box>
        </Box>
      </Box>

      {/* Brands Love Us Section */}
      <Box
        sx={{
          py: { xs: 4, sm: 5, md: 8 },
          px: { xs: 2, sm: 3, md: 6 },
          background: theme.palette.background.default ,
          textAlign: "center"
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontFamily: "Moret Variable",
            fontWeight: '600',
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "62px" },
            background: "linear-gradient(180deg, #0f172a 0%, #334155 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: { xs: 3, sm: 4, md: 6 },
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
          }}
        >
          Brands Love Us
        </Typography>

        <Box 
          sx={{ 
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2, md: 4 },
            flexWrap: "wrap"
          }}
        >
          {[...brandNames].reverse().map((brand, index) => (
            <Box
              key={brand.name}
              sx={{ overflow: "hidden", py: 1 }}
            >
              <motion.div
                animate={{
                  y: ["-100%", "0%", "0%", "100%"],
                  opacity: [0, 1, 1, 0]
                }}
                transition={{
                  duration: 2,
                  ease: "easeOut",
                  times: [0, 0.1, 0.9, 1],
                  repeat: Infinity,
                  repeatDelay: 1.5,
                  delay: index * 0.12
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <Box
                  sx={{
                    width: { xs: "24px", sm: "28px", md: "32px" },
                    height: { xs: "24px", sm: "28px", md: "32px" },
                    borderRadius: { xs: "6px", md: "8px" },
                    background: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "0.625rem", sm: "0.75rem", md: "0.875rem" },
                    fontWeight: 700,
                    color: "white",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                  }}
                >
                  {brand.icon}
                </Box>
                <Typography
                  sx={{
                    color: "#1e293b",
                    fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1.1rem" },
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    display: { xs: "none", sm: "block" }
                  }}
                >
                  {brand.name}
                </Typography>
              </motion.div>
            </Box>
          ))}
        </Box>
      

      {/* Bento Features Section */}
      <BentoSection />
      
      <Box ref={payRef}>
        <PaySection />
      </Box>

      {/* <SimpleTransition gradient="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" height="10vh" /> */}

      <Box ref={createRef}>
        <CreateSection />
      </Box>

     

      {/* Featured Articles Section - Integrated between Create and Collaborate */}
      <FeaturedArticles />

     

      <Box ref={collaborateRef}>
        <CollaborateSection />
      </Box>
      </Box>
      {!isIOS && <HomeFooter />}
    </Box>
  );
}